/**
 * A bookmark, on the wire.
 *
 * The central domain type, so the decisions in it are worth stating rather than
 * inferring from the field list.
 *
 * ── Absent is the empty string, not null ─────────────────────────────────────
 *
 * Four of the six fields are optional in practice, and every one of them is a
 * non-null `String` whose empty value means "not known". That is one representation
 * of one state, which is worth more here than the extra precision a null would
 * carry: there is no useful difference between a bookmark with no title and one
 * with a blank title — both render as the URL — and the alternative costs an
 * unwrap at every read site in Kotlin, Swift and JavaScript.
 *
 * The one place the distinction *would* matter is [aiSummary], where "not generated
 * yet" and "generated, and it came back empty" are different facts. They are folded
 * together deliberately: an empty summary from a generator is a failure, and the
 * right response to it is the same as never having run — try again.
 *
 * Contrast `UiSettingsState.settings`, where an absent key is genuinely not an
 * empty string. There, absence means "the user never chose", and choosing blank is
 * a thing a user can do. Here, nobody chooses a blank title.
 */
package se.soderbjorn.lunapin.clientserver

import kotlinx.serialization.Serializable

/**
 * Where a bookmark is in the enrichment queue.
 *
 * Five states rather than the single `aiProcessed` boolean the previous bookmark manager
 * used, and the difference is that this queue lives in a database a server restarts out
 * from under. A boolean can say "it ran"; it cannot say "it is in the queue", "something
 * has it right now", or "it gave up, and here is why" — and all three are things a person
 * looking at a row that has no summary needs to be told apart.
 *
 * Strings rather than a `@Serializable enum`, for [SummaryLengths]' reason: an enum
 * refuses an unknown value inside kotlinx-serialization, so a client one build behind a
 * sixth state would fail to parse a whole bookmark list rather than showing a state it
 * does not have a word for. The column's own CHECK constraint is what enforces the set,
 * on the one side that writes it.
 */
object AiStates {
    /** Nothing has been asked for. The state of every bookmark on a server with no key. */
    const val NONE: String = "none"

    /** In the queue. `ai_state = 'pending'` **is** the queue — see `Bookmarks.sq`. */
    const val PENDING: String = "pending"

    /** The worker has claimed it. Reset to [PENDING] at boot; see the server's worker. */
    const val RUNNING: String = "running"

    /** It was read, summarised and tagged. */
    const val DONE: String = "done"

    /** It was tried and will not be tried again. `Bookmark.aiState` is paired with a reason. */
    const val FAILED: String = "failed"

    /** Every value the column may hold. */
    val all: List<String> = listOf(NONE, PENDING, RUNNING, DONE, FAILED)
}

/**
 * One saved link.
 *
 * @property id the row's own identifier, assigned by the server. Clients quote it
 *   back to delete; nothing else uses it and nothing derives meaning from its order.
 * @property url the link itself, and the only field a save must carry. **Always
 *   `http` or `https`** — the server refuses anything else, which is not fussiness:
 *   a `javascript:` URL stored here and later rendered as an anchor in LunaPin's own
 *   page is a stored XSS on our own origin, and our own origin is where the session
 *   cookie lives.
 * @property title what the page called itself. Populated from `document.title` by
 *   the bookmarklet, which is the only place that can read it for free — see
 *   `Bookmarklet.kt`. Empty when the link arrived from somewhere that did not know.
 * @property createdAt when this device first saved it, epoch millis. Named for the
 *   two columns that already exist (`users.created_at`, `sessions.created_at`)
 *   rather than "creationDate", so the schema reads as one schema.
 *
 *   **Set once.** Re-saving a URL already in the collection updates what is known
 *   about it and leaves this alone: the fact worth keeping is when the link entered
 *   the collection, not when it was last re-added by a second press of the
 *   bookmarklet.
 * @property tags what the user filed it under.
 *
 *   A list rather than the comma-separated string this feature was first specified
 *   with, and the reason is the query it has to serve: "everything tagged `rust`".
 *   Against a string that is `LIKE '%rust%'`, which matches `rustic` and `trust`,
 *   cannot use an index at any size, and turns "which tags do I have?" into a scan
 *   and a split. Server-side they are rows in `bookmark_tags`, where each of those
 *   is an index seek — the table's own header carries the full argument.
 *
 *   Comma-separated survives where it belongs: as an *input* format, in the
 *   bookmarklet's text field and anywhere else a person types a list, parsed at the
 *   edge rather than stored.
 *
 *   **Every tag starts with `#`, and the server puts it there.** A client may send
 *   `rust` or `#rust`; both are stored as `#rust`, so the two are one tag rather than
 *   two and no surface has to remember to draw a marker of its own.
 *
 *   **And every tag is lower-cased.** `#Rust` and `#rust` are one tag, not two: a tag
 *   is a filing label rather than prose, and two labels differing only in a capital
 *   are two piles of the same thing — a collection nobody can keep tidy by hand. This
 *   is lossy on purpose, and worth stating plainly: `#JavaScript` comes back as
 *   `#javascript`, everywhere, including in an export. The alternative — keep the
 *   first spelling, match without case — makes the collection's labels depend on which
 *   bookmark happened to be saved first.
 *
 *   Searching is forgiving of both rules: `?tag=rust`, `?tag=#rust` and `?tag=RUST`
 *   are one query.
 *
 *   Trimmed, blanks dropped, de-duplicated, and **sorted** — a set has no order to
 *   preserve, so the order shown is one the reader can predict rather than the order
 *   somebody happened to type on the day.
 * @property summary what the user wrote about it. Never generated; a person's own
 *   note is not something a machine may overwrite, which is the whole reason
 *   [aiSummary] is a second field rather than this one being filled in.
 * @property aiSummary what a generator wrote about it, kept apart from [summary] for
 *   the reason above. Written by the server's enrichment worker and by nothing else: no
 *   client sends it and no route accepts it, which is what keeps "the user wrote this"
 *   and "a machine wrote this" from becoming two claims this server cannot tell apart.
 * @property aiState where this bookmark is in the enrichment queue — one of
 *   [AiStates.all]. [AiStates.NONE] on every bookmark of a deployment with no model
 *   provider key, and on every bookmark of an account that has turned the feature off,
 *   so a client can draw nothing at all without asking a second question.
 *
 *   Defaulted, so a client built before this field existed still parses.
 * @property aiError why the last enrichment gave up, in one sentence somebody can act on,
 *   or `""`. Non-empty only alongside [AiStates.FAILED]: every other state clears it, so
 *   there is no such thing as a stale reason sitting beside a summary that arrived.
 *
 *   **One sentence, written by this server, never the provider's raw body.** The failure
 *   most likely to reach here is a rejected API key, and a provider's error payload is the
 *   single most likely place for a fragment of that key to appear. The worker composes
 *   these itself for exactly that reason.
 *
 *   Held back deliberately until something drew it: a failed row shows the sentence on
 *   hover and offers *Try again*, which is what decided the field's shape. Defaulted, so
 *   the Android and iOS screens compile and render unchanged.
 * @property aiTags which of [tags] the model added rather than a person — **a subset of
 *   [tags], never a separate list.** That is the whole shape of this decision: AI tags
 *   are *applied*, not proposed, so they are already filed, already searchable and
 *   already in [tags]; this says only who put them there, so a surface that cares can
 *   draw one small mark and a surface that does not can ignore the field and be exactly
 *   as correct as it was before.
 *
 *   The alternative — a `suggestedTags` list beside [tags], waiting for approval — was
 *   rejected before any of this was written. It makes every reader merge two lists, and
 *   it makes a tag's existence depend on somebody pressing a button.
 *
 *   Server-side this is `bookmark_tags.origin`, a column on the junction row rather than
 *   on the tag, which is the right grain: the same tag can be yours on one bookmark and
 *   the model's on another.
 *
 *   Defaulted empty, so Android and iOS compile and render exactly as before.
 */
@Serializable
data class Bookmark(
    val id: Long,
    val url: String,
    val title: String = "",
    val createdAt: Long,
    val tags: List<String> = emptyList(),
    val summary: String = "",
    val aiSummary: String = "",
    val aiState: String = AiStates.NONE,
    val aiError: String = "",
    val aiTags: List<String> = emptyList(),
)

/**
 * Everything this account has saved.
 *
 * A wrapper around the list rather than a bare JSON array, so that the day this
 * needs paging or a total, the response grows a field instead of changing shape.
 *
 * @property bookmarks newest first. The order is the server's and not a hint: a
 *   client that re-sorted would disagree with the next page it asked for.
 */
@Serializable
data class BookmarkList(
    val bookmarks: List<Bookmark> = emptyList(),
)

/**
 * Save a link, or update the one already saved under this URL.
 *
 * **The URL is the identity here**, and that is what makes this an upsert: one URL is
 * one bookmark per account (see the unique constraint in `Bookmarks.sq`), so saving is
 * idempotent by construction and a second save of the same link folds into the first.
 * It is also what this request cannot do — changing a bookmark's URL through it creates
 * a second bookmark rather than moving the first. That, and the blank rule below, is
 * why [EditBookmarkRequest] exists.
 *
 * @property url required, and `http`/`https` only. Anything else is a 400 — see
 *   [Bookmark.url] for why that is a security rule rather than tidiness.
 * @property title optional. **Blank does not erase.** An empty title on a save of a
 *   URL already stored leaves the stored title alone, so re-pressing the bookmarklet
 *   on a page that has since lost its `<title>` does not throw away the good one.
 *   The same rule applies to [tags] and [summary].
 * @property tags optional, and **tri-state for [enrich]'s reason**: "I did not say" must
 *   not read as "none". Null, the default, is a caller with nowhere to have asked — a
 *   share sheet, an importer, a client one build behind — and the server fills in the
 *   account's `saveDefaultTags` for it, but **only for a URL this account has not saved
 *   yet**. A list, including an empty one, is an answer somebody gave: a save popup drew
 *   the defaults as chips, they un-pressed the ones they did not want, and what is left
 *   is what they meant. Filling defaults into an empty list would make them impossible
 *   to decline.
 *
 *   The "not saved yet" clause is what stops a second share of a link from replacing the
 *   tags it has been given since the first: defaults are what a *new* bookmark starts
 *   with, not a rule reapplied on every save. Without it, re-sharing a page from a phone
 *   would quietly overwrite the filing somebody did by hand.
 *
 *   Normalised by the server; see [Bookmark.tags]. An empty list on a save of a URL
 *   already stored leaves the stored tags alone, by the rule above — so this route
 *   cannot *clear* tags. [EditBookmarkRequest] can, and says so.
 *
 *   Absent on the wire is null rather than empty because the API client does not encode
 *   defaults, so a caller that never mentions tags omits the key — which is what makes
 *   the distinction above expressible without every client being changed to send it.
 * @property summary optional, the user's own note.
 * @property enrich whether to read and summarise this page in the background — the save
 *   popup's *AI on/off*, and **an override rather than a setting**. Null, the default,
 *   means "whatever this account chose", which is what every caller that has no such
 *   control should send: a client one build behind, a mobile share sheet, an importer.
 *   `false` is somebody saying *not this one*, for a page they know is behind a login or
 *   simply not worth a request. `true` is *this one anyway*, for a single save on an
 *   account that leaves the feature off.
 *
 *   **It cannot turn AI on where the deployment has no key.** There is nothing to call,
 *   so `true` on such a server is silently the same as `false` — see `AccountSettings`
 *   `aiAvailable`, which is how a screen knows not to offer the control at all.
 *
 *   A tri-state `Boolean?` rather than a boolean with a default, because the two are
 *   genuinely different facts: "I did not say" must not read as "no". A default of
 *   `false` would quietly turn the feature off for every client that has not heard of it.
 *
 * @property pageText the page's readable text, when the caller could see the page. Empty,
 *   the default, is what every caller that cannot read one sends, and it means exactly
 *   what it says: **read the page yourself**, which is what the server has always done.
 *
 *   ── Why a client sends a page the server could fetch ──────────────────────
 *
 *   Because for a whole class of pages it could not. The enrichment worker fetches from
 *   the *server*, so a page behind a sign-in answers it with a login screen, a page on a
 *   network the server cannot route to answers with a timeout, and a page that is a shell
 *   until JavaScript has run answers with the shell. The browser that saved the bookmark
 *   was looking at all three. So a client that has the text may hand it over, and the
 *   worker prefers it — see the server's `EnrichmentWorker.attempt` and the
 *   `supplied_text` column in its `Bookmarks.sq`.
 *
 *   Today that client is the Chrome extension, which reads the tab it was pressed on with
 *   the `activeTab` permission the press already grants and carries the text to the save
 *   popup in the URL's fragment. Nothing stops a share sheet or an importer doing the
 *   same; this field is the contract, not the extension's private arrangement.
 *
 *   ── What the server does with it, which is deliberately little ────────────
 *
 *   Collapses the whitespace, cuts it to the same 12,000 characters a fetched page is cut
 *   to, and ignores it below a floor where a loading shell is not a page. **Never
 *   refused**: this is an extra offered by a helpful client, and a save that came back
 *   400 because an article ran long would be a bookmark lost to the gesture. See the
 *   server's `normaliseSuppliedText`.
 *
 *   It is stored, and kept — for a page behind a sign-in the copy that arrives here is
 *   the only one that will ever exist, so *Regenerate* has something to read a second
 *   time. It is **not** on [Bookmark]: no screen draws an article, and a collection's
 *   listing must not carry one per row.
 *
 *   ── And it is not a summary ───────────────────────────────────────────────
 *
 *   The same rule [Bookmark.aiSummary]'s absence states, one step earlier. This is
 *   *material* — what the page said — and a model still has to be asked about it before
 *   anything lands in a column. A caller cannot use it to write a summary, or a tag, or
 *   anything else a machine is supposed to have decided.
 *
 * [Bookmark.aiSummary] is deliberately absent from this request, and stays absent now
 * that something does write it. A field a client could set would make "the user wrote
 * this" and "a machine wrote this" two claims the server cannot tell apart — which is the
 * one distinction the pair of fields exists to keep. [enrich] asks for a summary; it
 * cannot supply one.
 *
 * So is [Bookmark.aiState] — where a bookmark is in the queue is the server's own
 * bookkeeping, not something a caller may assert.
 */
@Serializable
data class SaveBookmarkRequest(
    val url: String,
    val title: String = "",
    val tags: List<String>? = null,
    val summary: String = "",
    val enrich: Boolean? = null,
    val pageText: String = "",
)

/**
 * Edit one bookmark, named by its id: `PUT` to [ApiRoutes.BOOKMARK_BY_ID].
 *
 * ── Every field is authoritative, including the blank ones ───────────────────
 *
 * The single difference from [SaveBookmarkRequest] and the reason both exist. There a
 * blank value means "I do not know" and leaves the stored one alone, which is right for
 * a bookmarklet that may know less than the database does. **Here a blank title clears
 * the title and an empty [tags] removes every tag**, because the caller is a form
 * somebody is looking at: they can see the field is empty, and they emptied it.
 *
 * ── Why not `PATCH` with absent-versus-null ──────────────────────────────────
 *
 * Because the caller already holds the whole bookmark. A partial-update protocol would
 * be machinery for a case that does not exist, and it would reintroduce exactly the
 * ambiguity this request was written to escape: "absent means leave alone" is the rule
 * that makes clearing a field impossible to express.
 *
 * The id is the identity, so [url] may be changed and the bookmark **moves** rather than
 * being duplicated. Moving it onto a URL this account has already saved is a 400 with a
 * sentence, because `UNIQUE (user_id, url)` still holds.
 *
 * [Bookmark.aiSummary] is absent for [SaveBookmarkRequest]'s reason, unchanged: nothing
 * a client sends may land in the column that records what a machine wrote. So is
 * [Bookmark.createdAt] — when a link entered the collection is not an editable fact.
 *
 * @property url required, `http`/`https` only, and validated exactly as a save is.
 * @property title what the bookmark is called now. Blank clears it.
 * @property tags the whole list, normalised by the server; see [Bookmark.tags]. Empty
 *   clears every tag. Tags already on this bookmark keep their recorded origin, so
 *   fixing a typo in a note does not quietly reattribute the model's tags to the person
 *   editing — see the server's `BookmarkStore.replaceTags`.
 * @property summary the user's own note. Blank clears it.
 */
@Serializable
data class EditBookmarkRequest(
    val url: String,
    val title: String = "",
    val tags: List<String> = emptyList(),
    val summary: String = "",
)
